import {
  API_TYPES,
  getShiftModalResponse,
  ShiftAuctionStatus,
  type ShiftModal,
} from "@clipboard-health/contract-worker-app-bff";
import { isDefined } from "@clipboard-health/util-ts";
import { useGetQuery } from "@src/appV2/api";
import { environmentConfig } from "@src/appV2/environment";
import { CbhFeatureFlag } from "@src/appV2/FeatureFlags/CbhFeatureFlag";
import { useCbhFlag } from "@src/appV2/FeatureFlags/useCbhFlag";
import { APP_V2_APP_EVENTS } from "@src/appV2/lib";
import { useGetShiftBoosts } from "@src/appV2/redesign/Shift/ShiftBoost/api/useGetShiftBoosts";
import { useDefinedWorker } from "@src/appV2/Worker/useDefinedWorker";
import { type QueryClient } from "@tanstack/react-query";
import axios from "axios";
import { minutesToMilliseconds, parseISO } from "date-fns";

import { useGetWorkplaceProfile } from "../../Workplace/useGetWorkplaceProfile";
import { mapShiftModalData } from "../convertModalShiftData";
import { convertOfferToModalShiftOfferData } from "../convertOpenShiftToModalShiftData";
import { getRefetchIntervalForAuctionShiftInMilliseconds } from "../ShiftAuction/utils";
import type { ModalShiftData } from "../types";
import { useAutoRefreshOffer } from "./useAutoRefreshOffer";

interface UseGetShiftModalOptions {
  enabled?: boolean;
  autoRefreshOffer?: boolean;
}

export const GET_SHIFT_MODAL_URL = `${environmentConfig.REACT_APP_WORKER_APP_BFF_URL}/shift-modals/`;

function getQueryUrl(shiftId?: string) {
  return GET_SHIFT_MODAL_URL + shiftId;
}

function getQueryParams() {
  return { include: [API_TYPES.shiftAuction, API_TYPES.shiftAuctionBid] };
}

/**
 * Fetches shift modal details and converts them to the modal shift data format.
 * Also fetches an auto-refreshed offer for the shift, if enabled.
 */

export function useGetShiftModal(shiftId?: string, options: UseGetShiftModalOptions = {}) {
  const { enabled = true, autoRefreshOffer = true } = options;
  const worker = useDefinedWorker();

  const checkShouldFetchRegularShift = enabled && isDefined(shiftId);

  const {
    data: shiftModal,
    isLoading: shiftModalIsLoading,
    isError: shiftModalIsError,
  } = useGetQuery({
    url: getQueryUrl(shiftId),
    responseSchema: getShiftModalResponse,
    meta: {
      logErrorMessage: APP_V2_APP_EVENTS.GET_OPEN_SHIFT_MODAL_FAILURE,
    },
    queryParams: { include: [API_TYPES.shiftAuction, API_TYPES.shiftAuctionBid] },
    enabled: checkShouldFetchRegularShift,
    staleTime: minutesToMilliseconds(1),
    // 404 is a legitimate error, so we don't want to show the error boundary in this case
    useErrorBoundary: (error) => {
      return !(axios.isAxiosError(error) && error.response?.status === 404);
    },
    // We don't want to retry the query if it's a 404
    retry: (_failureCount, error) => {
      return !(axios.isAxiosError(error) && error.response?.status === 404);
    },
    refetchInterval: (data) => {
      const shiftAuction = data?.included.find((entity) => entity?.type === API_TYPES.shiftAuction);
      const auctionEndTime = shiftAuction?.attributes.endTime;
      return getRefetchIntervalForAuctionShiftInMilliseconds({
        auctionEndTime: auctionEndTime ? parseISO(auctionEndTime) : undefined,
        auctionStatusIsOpen: shiftAuction?.attributes.status === ShiftAuctionStatus.OPEN,
      });
    },
  });

  const checkShouldFetchWorkplaceProfile = isDefined(shiftModal);

  const {
    data: workplace,
    isInitialLoading: workplaceProfileIsLoading,
    isError: workplaceProfileIsError,
  } = useGetWorkplaceProfile(
    {
      workplaceId: shiftModal?.data.attributes.workplace.id ?? "",
    },
    {
      enabled: checkShouldFetchWorkplaceProfile,
    }
  );

  const shiftAuction = shiftModal?.included
    .filter((entity) => isDefined(entity) && entity.type === API_TYPES.shiftAuction)
    .find(
      (entity) =>
        entity.id === shiftModal?.data.relationships.shiftAuction?.data.id &&
        entity.attributes.status === ShiftAuctionStatus.OPEN
    );
  const shiftAuctionBid = shiftAuction
    ? shiftModal?.included
        .filter((entity) => isDefined(entity) && entity.type === API_TYPES.shiftAuctionBid)
        .find((entity) => entity.id === shiftAuction.relationships.shiftAuctionBid?.data.id)
    : undefined;

  const {
    data: { offer },
    isLoading: offerIsLoading,
    isError: offerIsError,
  } = useAutoRefreshOffer(
    { shiftId },
    { enabled: autoRefreshOffer && enabled && !isDefined(shiftAuction) }
  );

  const shiftBoostConfig = useCbhFlag(CbhFeatureFlag.SHIFT_BOOST_CONFIG, {
    defaultValue: { enabled: false },
  });

  const { data: shiftBoosts, isLoading: shiftBoostsIsLoading } = useGetShiftBoosts(
    {
      workerId: worker.userId,
      statuses: ["AVAILABLE"],
    },
    { enabled: shiftBoostConfig.enabled }
  );

  const availableShiftBoost = shiftBoosts?.data[0];

  let shift: ModalShiftData | undefined;

  if (isDefined(shiftModal)) {
    shift = mapShiftModalData({
      // The Zod schema uses ZodEffects for unmetCriteria, making the inferred
      // response input type use unknown[] while ShiftModal uses the validated
      // output type. The data has been validated by Zod, so this cast is safe.
      shiftModal: shiftModal.data as ShiftModal,
      offer,
      workplaceFullAddress: workplace?.fullAddress,
      breakPolicy: shiftModal.data.attributes.shift.breakPolicy,
      shiftAuction,
      shiftAuctionBid,
    });
  }

  if (isDefined(shift) && isDefined(offer) && !isDefined(shift.shiftAuction)) {
    shift = {
      ...shift,
      offer: convertOfferToModalShiftOfferData(shift.attributes.durationInHours, offer),
    };
  }

  const isLoading =
    (checkShouldFetchRegularShift && shiftModalIsLoading) ||
    (checkShouldFetchWorkplaceProfile && workplaceProfileIsLoading) ||
    (shiftBoostConfig.enabled && shiftBoostsIsLoading);

  const shiftFailedLoading = isDefined(shiftId) && (shiftModalIsError || workplaceProfileIsError);

  const qualification =
    isDefined(offer) || !offerIsLoading
      ? (offer?.attributes.qualification ?? shift?.attributes.qualification)
      : undefined;

  return {
    data: { shift, availableShiftBoost, offer, qualification },
    isLoading,
    isError: shiftFailedLoading,
    offerIsError,
    offerIsLoading,
  };
}

export async function invalidateShiftModal(queryClient: QueryClient, shiftId?: string) {
  await queryClient.invalidateQueries({
    queryKey: [getQueryUrl(shiftId), getQueryParams()],
  });
}
